import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AuthGuard.css';

const AuthGuard = ({ 
  children, 
  fallback, 
  onUnauthenticated, 
  showSignupModal = true,
  className = '',
  ...props 
}) => {
  const { isAuthenticated, loading } = useAuth();
  const [showModal, setShowModal] = useState(false);

  // Show loading state while checking authentication
  if (loading) {
    return <div className={`auth-guard-loading ${className}`}>{children}</div>;
  }

  // If authenticated, render children normally
  if (isAuthenticated) {
    return <div className={className} {...props}>{children}</div>;
  }

  // Handle unauthenticated click
  const handleUnauthenticatedClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onUnauthenticated) {
      onUnauthenticated();
    } else if (showSignupModal) {
      setShowModal(true);
    }
  };

  // Custom fallback component
  if (fallback) {
    return (
      <div className={className} {...props}>
        {fallback}
      </div>
    );
  }

  // Default behavior: wrap children and show signup modal on click
  return (
    <>
      <div 
        className={`auth-guard-wrapper ${className}`} 
        onClick={handleUnauthenticatedClick}
        {...props}
      >
        {React.cloneElement(children, {
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            handleUnauthenticatedClick(e);
          }
        })}
      </div>
      
      {showSignupModal && showModal && (
        <SignupModal 
          onClose={() => setShowModal(false)}
          onSignup={() => {
            setShowModal(false);
            // Redirect to OAuth
            const isDevelopment = window.location.hostname === 'localhost';
            const oauthUrl = isDevelopment 
              ? 'https://localhost:5001/auth/google?return_to=/talkto' 
              : '/auth/google?return_to=/talkto';
            window.location.href = oauthUrl;
          }}
        />
      )}
    </>
  );
};

// Signup Modal Component
const SignupModal = ({ onClose, onSignup }) => {
  return (
    <div className="signup-modal-overlay" onClick={onClose}>
      <div className="signup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="signup-modal-header">
          <h2>Sign up to start chatting</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="signup-modal-content">
          <p>Sign up with Google to start chatting with AI agents and access all features.</p>
          
          <div className="signup-benefits">
            <div className="benefit">
              <span className="benefit-icon">💬</span>
              <span>Chat with AI agents</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">🤖</span>
              <span>Build your own agent</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">📊</span>
              <span>Track conversations</span>
            </div>
          </div>
          
          <button className="signup-button" onClick={onSignup}>
            <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google" />
            Continue with Google
          </button>
          
          <p className="signup-footer">
            By signing up, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthGuard;
